export type CompareFeatureRow = {
  label: string;
  lvlup: string | boolean;
  competitor: string | boolean;
  highlight?: boolean;
};

export type CompareTarget = {
  slug: string;
  competitor: string;
  displayName: string;
  tagline: string;
  metaTitle: string;
  metaDescription: string;
  heroHeadline: string;
  heroSub: string;
  positioning: string;
  pricingNote: string;
  switchReasons: Array<{ title: string; body: string }>;
  features: CompareFeatureRow[];
  faqs: Array<{ q: string; a: string }>;
  testimonialQuote?: string;
  testimonialAuthor?: string;
};

const SHARED_BASE_FEATURES = (competitorAppType: string): CompareFeatureRow[] => [
  { label: "Fully branded native iOS + Android app", lvlup: "Included", competitor: competitorAppType, highlight: true },
  { label: "Commission on package & subscription sales", lvlup: "0%", competitor: "Varies / yes", highlight: true },
  { label: "Time to launch in app stores", lvlup: "~20 days", competitor: "Weeks to months" },
  { label: "Exercise library (with video)", lvlup: "1,000+ included", competitor: "Varies" },
  { label: "Built-in nutrition planning + macros", lvlup: true, competitor: "Limited / add-on" },
  { label: "Sell packages & subscriptions", lvlup: true, competitor: "Partial" },
  { label: "Course creation & ready-made programs", lvlup: "High Value tier", competitor: false },
  { label: "Engagement challenges & habits", lvlup: true, competitor: "Limited" },
  { label: "Client check-ins, progress photos, body metrics", lvlup: true, competitor: true },
  { label: "Free trial", lvlup: "14 days", competitor: "Varies" },
];

export const COMPARE_TARGETS: CompareTarget[] = [
  {
    slug: "trainerize-alternative",
    competitor: "Trainerize",
    displayName: "Trainerize",
    tagline: "The branded-app, zero-commission Trainerize alternative",
    metaTitle: "Trainerize Alternative: Why Coaches Switch to LVLUP (2026)",
    metaDescription:
      "Looking for a Trainerize alternative? LVLUP gives coaches a fully branded native app, 0% commission on sales, and a 20-day launch. Try it free.",
    heroHeadline: "The Trainerize alternative built for coaches who want their own brand",
    heroSub:
      "Clients shouldn't have to download 'Trainerize' to work with you. With LVLUP, your app lives in the App Store and Google Play under your name — and you keep 100% of every sale.",
    positioning:
      "Trainerize is a shared marketplace app where every client downloads 'Trainerize' and finds their coach inside it. LVLUP gives every coach a fully branded native app — your logo, your name, your colors — published under your own brand. And LVLUP charges 0% commission on package and subscription sales, while Trainerize layers transaction fees on top.",
    pricingNote:
      "LVLUP starts at $48/month with a 14-day free trial. The Pro plan ($78/month) includes your fully branded iOS + Android app. Compare that to Trainerize's per-client pricing plus transaction fees.",
    switchReasons: [
      {
        title: "Your brand, not Trainerize's",
        body: "Clients open YOUR app — not a generic marketplace. Reinforce your brand every workout, every check-in, every notification.",
      },
      {
        title: "0% commission on every sale",
        body: "Trainerize takes a cut every time you sell a package. LVLUP doesn't. Keep 100% of revenue beyond your platform subscription.",
      },
      {
        title: "Launch in 20 days, not months",
        body: "We handle App Store submission, branding, and provisioning. Most coaches are live in stores within 20 days of signup.",
      },
      {
        title: "Built-in nutrition + sales + community",
        body: "No need to bolt on extra tools. Programming, nutrition planning, package sales, and challenges all in one platform.",
      },
    ],
    features: SHARED_BASE_FEATURES("Shared marketplace app"),
    faqs: [
      {
        q: "Is LVLUP a true Trainerize alternative?",
        a: "Yes — LVLUP covers everything you use Trainerize for (programming, client tracking, messaging, check-ins, package sales) and adds a fully branded native app, built-in nutrition planning, and 0% commission on sales.",
      },
      {
        q: "Can I migrate my clients from Trainerize to LVLUP?",
        a: "Yes. We can help you import client lists and historical program data. Most coaches migrate within 7–14 days and report higher client engagement once clients are inside the coach's own branded app.",
      },
      {
        q: "Does LVLUP charge a commission on sales like Trainerize?",
        a: "No. LVLUP charges 0% commission on package and subscription sales — you keep 100% of revenue beyond your monthly platform subscription (minus standard payment processor fees).",
      },
      {
        q: "How long does it take to get my branded app vs. Trainerize?",
        a: "LVLUP coaches typically go live in app stores within 20 days. Trainerize does not offer a true coach-branded native app — clients use the shared Trainerize app.",
      },
    ],
  },
  {
    slug: "truecoach-alternative",
    competitor: "TrueCoach",
    displayName: "TrueCoach",
    tagline: "The mobile-first, branded-app TrueCoach alternative",
    metaTitle: "TrueCoach Alternative: LVLUP vs TrueCoach for Coaches (2026)",
    metaDescription:
      "Looking for a TrueCoach alternative? LVLUP delivers a fully branded mobile app, built-in nutrition planning, and 0% commission on sales.",
    heroHeadline: "The TrueCoach alternative for coaches who lead with mobile",
    heroSub:
      "TrueCoach is built around a web dashboard. LVLUP is mobile-first — your clients get a polished native iOS + Android app under your brand, with nutrition, package sales, and community built in.",
    positioning:
      "TrueCoach is a web-first coaching dashboard with a generic mobile client app. LVLUP is mobile-first and gives every coach their own native branded app. LVLUP also includes nutrition planning, package and subscription sales with 0% commission, and course creation — features TrueCoach either lacks natively or sells as add-ons.",
    pricingNote:
      "LVLUP starts at $48/month with unlimited clients on every plan and a 14-day free trial — no surprise overage fees as you grow.",
    switchReasons: [
      {
        title: "A branded native app, not a shared one",
        body: "Your clients open YOUR app, not 'TrueCoach'. Brand consistency across web, mobile, push notifications, and the app icon on their home screen.",
      },
      {
        title: "Nutrition planning built in",
        body: "Macros, meal plans, and recipes are native — no need to glue on a separate nutrition tool.",
      },
      {
        title: "Sell packages with 0% commission",
        body: "Built-in checkout for one-off packages and recurring subscriptions, with zero platform commission on sales.",
      },
      {
        title: "Designed mobile-first",
        body: "Workout flow, check-ins, and messaging are built for the way coaches and clients actually use their phones day to day.",
      },
    ],
    features: SHARED_BASE_FEATURES("Generic client app"),
    faqs: [
      {
        q: "Is LVLUP a good TrueCoach alternative for personal trainers?",
        a: "Yes. LVLUP covers all the core TrueCoach use cases (programming, client tracking, video form review, messaging) and adds a branded native app, nutrition planning, and built-in package sales.",
      },
      {
        q: "Does LVLUP have video form review like TrueCoach?",
        a: "Yes — clients can record and send video, and coaches can review and respond inline from web or mobile.",
      },
      {
        q: "Can I import my TrueCoach clients?",
        a: "Yes. Our onboarding team helps migrate client lists and historical program data so you can switch without losing context.",
      },
      {
        q: "Is nutrition included or an add-on?",
        a: "Included. LVLUP has built-in macro tracking, meal plans, and a recipe database — no extra tool or fee required.",
      },
    ],
  },
  {
    slug: "hubfit-alternative",
    competitor: "Hubfit",
    displayName: "Hubfit",
    tagline: "The faster-to-launch, all-in-one Hubfit alternative",
    metaTitle: "Hubfit Alternative: LVLUP vs Hubfit for Branded Apps",
    metaDescription:
      "Looking for a Hubfit alternative? LVLUP offers branded coaching apps from $48/mo, a 20-day launch, built-in nutrition, and 0% commission.",
    heroHeadline: "The Hubfit alternative with transparent pricing and faster launch",
    heroSub:
      "Hubfit offers branded apps but at premium tiers. LVLUP gives you the same brand ownership starting at $48/month, with a 20-day launch and a 14-day free trial.",
    positioning:
      "Hubfit and LVLUP both offer branded coaching apps — but LVLUP is built for transparency and speed. Lower entry pricing, a fixed 20-day app store launch, included nutrition planning, and 0% commission on sales. Plus, the High Value Coaching tier adds course creation and ready-made program sales for coaches scaling premium offers.",
    pricingNote:
      "LVLUP starts at $48/month. The Pro plan ($78/month) includes your fully branded native app — typically lower than equivalent Hubfit tiers — and every plan has a 14-day free trial.",
    switchReasons: [
      {
        title: "Lower entry pricing",
        body: "Start at $48/month with a 14-day free trial. No long onboarding contracts, no setup fees.",
      },
      {
        title: "20-day app store launch",
        body: "We handle App Store and Google Play submission. Most coaches go live within 20 days.",
      },
      {
        title: "Nutrition + courses + challenges included",
        body: "Macro tracking, meal plans, ready-made program sales, and engagement challenges — all in one platform.",
      },
      {
        title: "0% commission on all sales",
        body: "Sell packages and subscriptions directly through your app and keep 100% of revenue beyond your subscription.",
      },
    ],
    features: SHARED_BASE_FEATURES("Branded app (premium tier)"),
    faqs: [
      {
        q: "How is LVLUP different from Hubfit?",
        a: "Both offer branded apps. LVLUP differentiates on lower entry pricing, a fixed 20-day launch timeline, included nutrition planning, course creation in the High Value tier, and 0% commission on sales.",
      },
      {
        q: "Is the pricing really lower than Hubfit?",
        a: "LVLUP's branded app plan starts at $78/month after a 14-day free trial. Hubfit's branded-app tier is typically priced higher. Compare your specific quote against our public pricing at /pricing.",
      },
      {
        q: "Can I scale to multiple coaches under one brand?",
        a: "Yes — the High Value Coaching plan supports multiple team members under a single branded app, ideal for agencies.",
      },
      {
        q: "Do you handle App Store and Google Play submission?",
        a: "Yes. We submit and maintain your app under your developer accounts, with full white-label branding.",
      },
    ],
  },
  {
    slug: "kahunas-alternative",
    competitor: "Kahunas",
    displayName: "Kahunas",
    tagline: "The transparent, no-commission Kahunas alternative",
    metaTitle: "Kahunas Alternative: LVLUP for High-Ticket Coaches",
    metaDescription:
      "Looking for a Kahunas alternative? LVLUP offers branded coaching apps from $48/mo, 0% commission, and a 14-day free trial for high-ticket coaches.",
    heroHeadline: "The Kahunas alternative built for high-ticket coaches who own their margin",
    heroSub:
      "Kahunas targets premium coaches at premium prices. LVLUP delivers the same caliber of branded experience with transparent pricing, 0% commission, and a dedicated success manager on the High Value tier.",
    positioning:
      "Kahunas is a premium coaching platform aimed at high-ticket coaches at high price points. LVLUP gives you a comparable branded experience with transparent public pricing, 0% commission on sales, and the High Value Coaching tier — which adds course creation, ready-made program sales, engagement challenges, and a dedicated success manager for scaling premium offers.",
    pricingNote:
      "LVLUP's High Value Coaching tier is built specifically for premium coaches scaling high-ticket programs — without the opaque enterprise pricing common in this category.",
    switchReasons: [
      {
        title: "Transparent pricing",
        body: "Public pricing starting at $48/month with a 14-day free trial. No discovery-call-required pricing games.",
      },
      {
        title: "0% commission, even on high-ticket sales",
        body: "Sell a $5,000 program and keep all $5,000 (minus standard payment processor fees). LVLUP never takes a cut.",
      },
      {
        title: "Course creation + ready-made programs",
        body: "The High Value tier includes course creation, ready-made program sales, and engagement challenges — perfect for downsells and signature offers.",
      },
      {
        title: "Dedicated success manager",
        body: "High Value Coaching includes a dedicated success manager to help optimize your offer, retention, and platform usage.",
      },
    ],
    features: SHARED_BASE_FEATURES("Branded app (enterprise)"),
    faqs: [
      {
        q: "Is LVLUP a good Kahunas alternative for premium coaches?",
        a: "Yes. The High Value Coaching tier is purpose-built for high-ticket coaches: course creation, ready-made program sales, engagement challenges, advanced branding, and a dedicated success manager — with transparent pricing.",
      },
      {
        q: "Can I sell high-ticket signature programs through LVLUP?",
        a: "Yes — package and subscription sales support any price point, with 0% platform commission. Combine with ready-made programs as downsells to capture more of the market.",
      },
      {
        q: "Do I get a dedicated point of contact?",
        a: "On the High Value Coaching plan, yes — you get a dedicated success manager who helps optimize your offer and platform usage.",
      },
      {
        q: "How does LVLUP's pricing compare to Kahunas?",
        a: "LVLUP publishes pricing transparently from $48/month. Premium tiers scale with the features you need, not with opaque enterprise contracts.",
      },
    ],
  },
  {
    slug: "everfit-alternative",
    competitor: "Everfit",
    displayName: "Everfit",
    tagline: "The flat-price, unlimited-client Everfit alternative",
    metaTitle: "Everfit Alternative: LVLUP vs Everfit for Coaches (2026)",
    metaDescription:
      "Looking for an Everfit alternative? LVLUP offers flat pricing, unlimited clients, built-in sales and nutrition, and a done-for-you branded app.",
    heroHeadline: "The Everfit alternative with flat pricing and unlimited clients",
    heroSub:
      "Everfit is a polished, affordable platform — but its pricing scales with every client you add, and tools like payments and automation come as paid add-ons. LVLUP is flat-rate with unlimited clients on every plan, and bundles your branded app, sales, and nutrition in.",
    positioning:
      "Everfit is a well-built coaching platform with a strong free tier, branded apps on its paid plans, nutrition, and a 1,000+ exercise library — a genuinely good product. The real difference is the pricing model and what's bundled: Everfit charges per client as you grow and sells payments, automation, and some nutrition as add-ons, while LVLUP is flat-rate with unlimited clients on every plan and includes package & subscription sales, nutrition, course creation, and a done-for-you branded app launch in one predictable price.",
    pricingNote:
      "Everfit starts lower (Pro from roughly $16/mo) but bills per client as you scale and charges extra for the payments and automation add-ons. LVLUP is flat — $48/mo Basic, $78/mo Pro — with unlimited clients and sales included, so your cost stays predictable as your roster grows.",
    switchReasons: [
      {
        title: "Flat pricing, unlimited clients",
        body: "LVLUP is a flat $48–$78/mo with unlimited clients on every plan — your bill doesn't climb each time you add a client, unlike per-client tiered pricing.",
      },
      {
        title: "Sales & automation included, not add-ons",
        body: "Package and subscription sales, challenges, and nutrition are built into your plan — no separate paid add-ons to switch on.",
      },
      {
        title: "Done-for-you branded app launch",
        body: "We build and submit your fully branded iOS + Android app to both stores under your name — most coaches are live within ~20 days.",
      },
      {
        title: "Course creation & ready-made program sales",
        body: "The High Value tier adds course building and ready-made program sales, so you can reach clients you can't coach one-to-one.",
      },
    ],
    // Competitor cells verified against everfit.io pricing (Jun 2026): free Starter
    // (5 clients), Pro per-client tiers, branded apps on Pro/Studio, Payments &
    // Autoflow as paid add-ons. Re-check before publishing — their plans change.
    features: [
      { label: "Pricing model", lvlup: "Flat rate", competitor: "Per-client tiers", highlight: true },
      { label: "Unlimited clients on every plan", lvlup: true, competitor: "Scales by client count", highlight: true },
      { label: "Package & subscription sales", lvlup: "Included", competitor: "Paid add-on" },
      { label: "Done-for-you branded app store launch", lvlup: "~20 days", competitor: "Self-serve" },
      { label: "Fully branded iOS + Android app", lvlup: "Included (Pro)", competitor: "Pro & Studio plans" },
      { label: "Built-in nutrition planning + macros", lvlup: true, competitor: "Yes (Pro+/add-on)" },
      { label: "Course creation & ready-made program sales", lvlup: "High Value tier", competitor: "Limited" },
      { label: "Engagement challenges & habits", lvlup: true, competitor: "Yes (add-on)" },
      { label: "Exercise library (with video)", lvlup: "1,000+ included", competitor: "1,000+" },
      { label: "Free option / trial", lvlup: "14-day trial", competitor: "Free plan (5 clients)" },
    ],
    faqs: [
      {
        q: "Is LVLUP a good Everfit alternative for coaches?",
        a: "Yes — especially as you scale. LVLUP covers the same core coaching tools (programming, tracking, messaging, check-ins, nutrition) and adds flat pricing with unlimited clients, built-in sales, and course creation, so your costs stay predictable as your roster grows.",
      },
      {
        q: "How is LVLUP's pricing different from Everfit's?",
        a: "Everfit starts lower but bills per client as you grow and sells payments and automation as add-ons. LVLUP is flat ($48–$78/mo) with unlimited clients and package sales included — so a growing roster doesn't keep increasing your bill.",
      },
      {
        q: "Does LVLUP include a branded app like Everfit?",
        a: "Yes. Both offer branded apps; LVLUP includes a fully branded iOS + Android app on the Pro plan and handles App Store and Google Play submission for you, typically within ~20 days.",
      },
      {
        q: "Can I migrate my clients from Everfit to LVLUP?",
        a: "Yes. Our onboarding team helps import client lists and program data so you can switch without losing history.",
      },
    ],
  },
  {
    slug: "coach-catalyst-alternative",
    competitor: "Coach Catalyst",
    displayName: "Coach Catalyst",
    tagline: "The branded-app, flat-price Coach Catalyst alternative",
    metaTitle: "Coach Catalyst Alternative: LVLUP vs Coach Catalyst (2026)",
    metaDescription:
      "Looking for a Coach Catalyst alternative? LVLUP gives coaches a fully branded native app, flat pricing with unlimited clients, and 0% commission. Try it free.",
    heroHeadline: "The Coach Catalyst alternative for coaches who've outgrown habit-only coaching",
    heroSub:
      "Coach Catalyst is excellent at habit and accountability coaching. LVLUP keeps that check-in muscle and adds a fully branded native app, deeper programming, built-in nutrition, and flat pricing with 0% commission.",
    positioning:
      "Coach Catalyst is a habit-first accountability platform: yes-or-no habit tracking, compliance calendars, done-for-you challenges and a lessons library, priced by client-count tier with a branded app available as an optional add-on. LVLUP is built the other way round. Every coach gets a fully branded native iOS + Android app on the Pro plan, with habit and metric tracking, structured check-ins, a 1,000+ exercise library, nutrition planning and package sales included, at a flat price that does not rise with your roster, and 0% commission on sales.",
    pricingNote:
      "LVLUP starts at $48/month with a 14-day free trial; the Pro plan ($78/month) includes your fully branded iOS + Android app and unlimited clients. Coach Catalyst prices by client-count tier and offers its branded app as an optional add-on, so compare the total at your 12-month roster target, not at today's client count.",
    switchReasons: [
      {
        title: "Your accountability system survives the switch",
        body: "Habit and metric tracking, structured check-ins, and in-app messaging are core LVLUP features, not bolt-ons. The workflow that made Coach Catalyst work for you transfers cleanly.",
      },
      {
        title: "A branded app that is included, not an add-on",
        body: "The Pro plan includes your fully branded native app under your own name in the App Store and Google Play. We handle submission; most coaches are live in about 20 days.",
      },
      {
        title: "The programming depth you were missing",
        body: "A 1,000+ exercise library with video, a full program builder, and built-in nutrition planning with macros, alongside the habit coaching you already run.",
      },
      {
        title: "Flat pricing with 0% commission",
        body: "Unlimited clients on every plan and no commission on packages or subscriptions. Growing your roster grows your margin, not your software bill.",
      },
    ],
    features: [
      { label: "Fully branded native iOS + Android app", lvlup: "Included (Pro)", competitor: "Optional add-on", highlight: true },
      { label: "Pricing model", lvlup: "Flat, unlimited clients", competitor: "Per-client tiers", highlight: true },
      { label: "Commission on package & subscription sales", lvlup: "0%", competitor: "Processor fees apply", highlight: true },
      { label: "Habit & accountability tracking", lvlup: true, competitor: true },
      { label: "Structured client check-ins", lvlup: true, competitor: true },
      { label: "Exercise library (with video)", lvlup: "1,000+ included", competitor: "Modifiable library" },
      { label: "Built-in nutrition planning + macros", lvlup: true, competitor: "Via integrations" },
      { label: "Sell packages & subscriptions in-app", lvlup: true, competitor: true },
      { label: "Course creation & ready-made programs", lvlup: "High Value tier", competitor: "Lessons + challenges" },
      { label: "Time to launch in app stores", lvlup: "~20 days", competitor: "Varies (add-on)" },
      { label: "Free trial", lvlup: "14 days", competitor: "14 days" },
    ],
    faqs: [
      {
        q: "Is LVLUP a good Coach Catalyst alternative for habit-based coaches?",
        a: "Yes. Habit and metric tracking, structured check-in workflows, and in-app messaging are core LVLUP features, so an accountability-first coaching model transfers cleanly. You then add programming, nutrition, and package sales inside the same branded app.",
      },
      {
        q: "How does LVLUP's pricing differ from Coach Catalyst's?",
        a: "Coach Catalyst prices by client-count tier, so your bill steps up as your roster grows, and its branded app is an optional add-on. LVLUP is flat ($48 to $78/month) with unlimited clients, the branded app included on Pro, and 0% commission on sales.",
      },
      {
        q: "Does Coach Catalyst have a branded app?",
        a: "A custom branded app is available from Coach Catalyst as an optional add-on. On LVLUP the fully branded iOS + Android app is included in the Pro plan, and we handle App Store and Google Play submission, typically within about 20 days.",
      },
      {
        q: "Can I migrate my clients from Coach Catalyst to LVLUP?",
        a: "Yes. Our onboarding team helps import client lists and program data so you can switch without losing history, and you can run one real client week on LVLUP check-ins before moving everyone.",
      },
    ],
  },
];

export function getCompareTarget(slug: string): CompareTarget | undefined {
  return COMPARE_TARGETS.find((t) => t.slug === slug);
}
